import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, MoreHorizontal, Mail, Trash2, UserCog, Loader2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from '../components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { useUsers, InviteUserDialog } from '../features/users';
import type { User } from '../features/users';

export function Users() {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
  const [actionMessage, setActionMessage] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);

  const {
    users,
    isLoading,
    error,
    fetchUsers,
    inviteUser,
    updateUserRole,
    removeUser,
    resendInvite,
  } = useUsers();

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const filteredUsers = users.filter(
    (user) =>
      (user.name?.toLowerCase() || '').includes(search.toLowerCase()) ||
      user.email.toLowerCase().includes(search.toLowerCase())
  );

  const getStatusColor = (status: User['status']) => {
    switch (status) {
      case 'active':
        return 'bg-green-500';
      case 'invited':
        return 'bg-yellow-500';
      case 'suspended':
        return 'bg-red-500';
    }
  };

  const handleInvite = async (email: string, role: 'admin' | 'member') => {
    await inviteUser({ email, role });
    setActionMessage({ type: 'success', text: t('users.inviteSent') });
    setTimeout(() => setActionMessage(null), 3000);
  };

  const handleRoleChange = async (userId: string, newRole: 'owner' | 'admin' | 'member') => {
    try {
      await updateUserRole(userId, newRole);
      setActionMessage({ type: 'success', text: t('users.roleUpdated') });
      setTimeout(() => setActionMessage(null), 3000);
    } catch {
      setActionMessage({ type: 'error', text: t('users.roleUpdateFailed') });
      setTimeout(() => setActionMessage(null), 3000);
    }
  };

  const handleRemoveUser = async (userId: string) => {
    if (!window.confirm(t('users.confirmRemove'))) return;

    try {
      await removeUser(userId);
      setActionMessage({ type: 'success', text: t('users.userRemoved') });
      setTimeout(() => setActionMessage(null), 3000);
    } catch {
      setActionMessage({ type: 'error', text: t('users.removeFailed') });
      setTimeout(() => setActionMessage(null), 3000);
    }
  };

  const handleResendInvite = async (userId: string) => {
    try {
      await resendInvite(userId);
      setActionMessage({ type: 'success', text: t('users.inviteResent') });
      setTimeout(() => setActionMessage(null), 3000);
    } catch {
      setActionMessage({ type: 'error', text: t('users.resendFailed') });
      setTimeout(() => setActionMessage(null), 3000);
    }
  };

  const getInitials = (user: User) => {
    if (user.name) {
      return user.name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase();
    }
    return user.email[0].toUpperCase();
  };

  const formatLastActive = (lastActiveAt?: string) => {
    if (!lastActiveAt) return null;
    const date = new Date(lastActiveAt);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffHours < 1) return t('users.justNow');
    if (diffHours < 24) return t('users.hoursAgo', { count: diffHours });
    if (diffDays < 7) return t('users.daysAgo', { count: diffDays });
    return date.toLocaleDateString();
  };

  if (isLoading && users.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t('users.title')}</h1>
          <p className="text-muted-foreground">{t('users.subtitle')}</p>
        </div>
        <Button onClick={() => setIsInviteDialogOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          {t('users.inviteUser')}
        </Button>
      </div>

      {actionMessage && (
        <div
          className={`p-4 rounded-lg ${
            actionMessage.type === 'success'
              ? 'bg-green-50 text-green-800 border border-green-200'
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}
        >
          {actionMessage.text}
        </div>
      )}

      {error && (
        <div className="p-4 rounded-lg bg-red-50 text-red-800 border border-red-200">{error}</div>
      )}

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>{t('users.teamMembers')}</CardTitle>
              <CardDescription>
                {filteredUsers.length} {t('users.memberCount', { count: filteredUsers.length })}
              </CardDescription>
            </div>
            <Input
              placeholder={t('common.search')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-64"
            />
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {filteredUsers.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">{t('users.noUsers')}</p>
            ) : (
              filteredUsers.map((user) => (
                <div
                  key={user.id}
                  className="flex items-center justify-between p-4 rounded-lg border"
                >
                  <div className="flex items-center gap-4">
                    <Avatar>
                      <AvatarImage src={user.avatarUrl} />
                      <AvatarFallback>{getInitials(user)}</AvatarFallback>
                    </Avatar>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium">{user.name || user.email}</p>
                        <div
                          className={`h-2 w-2 rounded-full ${getStatusColor(user.status)}`}
                          title={t(`users.${user.status}`)}
                        />
                      </div>
                      <p className="text-sm text-muted-foreground">{user.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-sm font-medium capitalize">{t(`users.${user.role}`)}</p>
                      {user.lastActiveAt && (
                        <p className="text-xs text-muted-foreground">
                          {formatLastActive(user.lastActiveAt)}
                        </p>
                      )}
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {user.role !== 'owner' && (
                          <DropdownMenuSub>
                            <DropdownMenuSubTrigger>
                              <UserCog className="mr-2 h-4 w-4" />
                              {t('users.changeRole')}
                            </DropdownMenuSubTrigger>
                            <DropdownMenuSubContent>
                              <DropdownMenuItem
                                onClick={() => handleRoleChange(user.id, 'admin')}
                                disabled={user.role === 'admin'}
                              >
                                {t('users.admin')}
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => handleRoleChange(user.id, 'member')}
                                disabled={user.role === 'member'}
                              >
                                {t('users.member')}
                              </DropdownMenuItem>
                            </DropdownMenuSubContent>
                          </DropdownMenuSub>
                        )}
                        {user.status === 'invited' && (
                          <DropdownMenuItem onClick={() => handleResendInvite(user.id)}>
                            <Mail className="mr-2 h-4 w-4" />
                            {t('users.resendInvite')}
                          </DropdownMenuItem>
                        )}
                        {user.role !== 'owner' && (
                          <DropdownMenuItem
                            className="text-destructive"
                            onClick={() => handleRemoveUser(user.id)}
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            {t('users.removeUser')}
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      <InviteUserDialog
        isOpen={isInviteDialogOpen}
        onClose={() => setIsInviteDialogOpen(false)}
        onInvite={handleInvite}
      />
    </div>
  );
}
